import { AppBarEvents } from "@1js/office-start-appbar-events";
import { AppBarInteractivity } from "@1js/office-start-appbar-types";
import { Divider, mergeClasses } from "@fluentui/react-components";
import { useLazyLoadQuery, useMutation, useNovaEventing } from "@nova/react";
import React, { createRef, useCallback, useMemo, useRef } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { useDebouncedCallback } from "use-debounce";
import type { AppBarControlQuery } from "./__generated__/AppBarControlQuery.types.graphql";
import type { AppBarMovePinnedAppMutation } from "./__generated__/AppBarMovePinnedAppMutation.types.graphql";
import type { AppBarPinAppMutation } from "./__generated__/AppBarPinAppMutation.types.graphql";
import type { AppBarQuery } from "./__generated__/AppBarQuery.types.graphql";
import type { AppBarUnpinAppMutation } from "./__generated__/AppBarUnpinAppMutation.types.graphql";
import { useAppBarStyles } from "./AppBar.styles";
import {
  AppBarProps,
  controlModelQuery,
  movePinPositionMutation,
  pinAppMutation,
  unpinAppMutation,
} from "./AppBar.types";
import { AppBarContextProvider } from "./AppBarContext";
import { AppBarItem } from "./AppBarContext.types";
import { AppBarErrorBoundary } from "./AppBarErrorBoundary";
import { AppBarImpression } from "./AppBarImpression";
import {
  AppBarBottomMargin,
  AppBarDividerMaxHeightValue,
  AppBarRockSteadyBottomMargin,
  AppBarRockSteadyTopMargin,
  AppBarVisualRefreshTopMargin,
  FlyoutTileID,
  MinTiles,
  TileDimensionDefaultValue,
  TileHeightMercuryValue,
  TileHeightVisualRefreshValue,
  TileHeightWideValue,
  TileMarginDefaultValue,
  ViewMode,
} from "./Constants";
import { FlexPanel } from "./FlexPanel";
import { KeyNavDir } from "./FlexPanel.types";
import { FlyoutButton } from "./FlyoutButton";
import useAppBarContext from "./hooks/useAppBarContext";
import { useDynamicMediaQueryListener } from "./hooks/useMediaQueryListener";
import { LaunchTile } from "./LaunchTile";
import { LazyTileContextMenu } from "./LazyTileContextMenu";
import { LazyTileContextMenuBehavior } from "./LazyTileContextMenu.types";
import { TileContextMenuCallbacks } from "./TileContextMenu.types";
import { TileSelectionBar } from "./TileSelectionBar";
import { TileSelectionBarRef } from "./TileSelectionBar.types";
export type M365ApplicationEdge =
  AppBarQuery["response"]["m365Apps"]["edges"][number];
export type M365ApplicationAcquisitionLinks =
  AppBarQuery["response"]["m365Apps"]["appAcquisitionLinks"];
export type AppBarControlModelData =
  AppBarControlQuery["response"]["views"]["appBarControl"];
/**
 * Navigation control that renders launch tiles that navigate to associated apps
 */
export const AppBarRender: React.FC<AppBarProps> = ({
  matchMedia = window.matchMedia, // Default to the browser window implementation
  customImportHandler = (p) => p,
  assignShortcutHandler,
  unassignShortcutHandler,
  onAppTileHoverEnter,
  onAppTileHoverLeave,
  backgroundShade,
  forwardRef,
  brandLogo,
}) => {
  const [mediaQueryReady, setMediaQueryReady] = React.useState(false);
  const appBarContext = useAppBarContext();
  const appBarResizeObserverRef = useRef<ResizeObserver>();
  const selectionBar = createRef<TileSelectionBarRef>();
  const contextMenuRef = useRef<LazyTileContextMenuBehavior>(null);
  const flyoutButtonRef = React.useRef<HTMLAnchorElement | HTMLButtonElement>(
    null
  );
  const appbarContainerRef = React.useRef<HTMLElement>();
  const appBarControlModelData = useLazyLoadQuery<AppBarControlQuery>(
    controlModelQuery,
    {}
  );
  const [pinM365Application] =
    useMutation<AppBarPinAppMutation>(pinAppMutation);
  const [dndInUse, setDnDInUse] = React.useState(false);
  const [dividerVisibleState, setDividerVisibleState] = React.useState(true);
  const controlModelData: AppBarControlModelData | null = useMemo(() => {
    if (appBarControlModelData.error) {
      throw appBarControlModelData.error;
    } else if (!appBarControlModelData?.data) {
      // Loading
      return null;
    }
    return appBarControlModelData.data?.views.appBarControl;
  }, [appBarControlModelData.data, appBarControlModelData.error]);
  const pinApp = React.useCallback(
    (applicationID: string) => {
      pinM365Application({
        variables: {
          applicationID: applicationID,
        },
        optimisticResponse: controlModelData?.enableOptimisticMutations
          ? {
              // Generate an optimistic response for when pinning succeeds, in which case
              // the newly pinned app's pinned index would get the max pinned index + 1 value.
              pinM365Application: {
                updatedM365Applications:
                  appBarContext.appBarData?.m365Apps?.edges?.map((edge) => {
                    const clonedNode = {
                      ...edge.node,
                    };
                    if (edge.node?.id === applicationID) {
                      clonedNode.pinnedIndex =
                        appBarContext.maxPinnedIndex != null
                          ? appBarContext.maxPinnedIndex + 1
                          : 0;
                    }
                    return clonedNode;
                  }) ?? [],
                __typename: "M365ApplicationPinStatusUpdateResult",
              },
            }
          : undefined,
      });
    },
    [
      pinM365Application,
      appBarContext.appBarData,
      appBarContext.maxPinnedIndex,
      controlModelData?.enableOptimisticMutations,
    ]
  );
  const [unpinM365Application] =
    useMutation<AppBarUnpinAppMutation>(unpinAppMutation);
  const unpinApp = React.useCallback(
    (applicationID: string) => {
      unpinM365Application({
        variables: {
          applicationID: applicationID,
        },
        optimisticResponse: controlModelData?.enableOptimisticMutations
          ? {
              // Generate an optimistic response for when unpinning succeeds, in which case
              // the unpinned app's pinned index would get set to -1
              unpinM365Application: {
                updatedM365Applications:
                  appBarContext.appBarData?.m365Apps?.edges?.map((edge) => {
                    const clonedNode = {
                      ...edge.node,
                    };
                    if (edge.node?.id === applicationID) {
                      clonedNode.pinnedIndex = -1;
                    }
                    return clonedNode;
                  }) ?? [],
                __typename: "M365ApplicationPinStatusUpdateResult",
              },
            }
          : undefined,
      });
    },
    [
      unpinM365Application,
      appBarContext.appBarData,
      controlModelData?.enableOptimisticMutations,
    ]
  );
  const [movePinnedM365Application] = useMutation<AppBarMovePinnedAppMutation>(
    movePinPositionMutation
  );
  const [appBarItemsById, appbarItemsByIdLookUp] = React.useMemo(() => {
    const appBarItemIds: Array<string> = [];
    const appbarItemsByIdLookUp: Record<string, string> = {};
    // if there are no pinned items, return an empty array
    if (!appBarContext.pinnedItems) {
      return [appBarItemIds, appbarItemsByIdLookUp];
    }
    // push all of the pinned items into the array
    appBarContext.pinnedItems.map((item) => {
      appBarItemIds.push(item.data.node.id);
      appbarItemsByIdLookUp[item.data.node.id] = item.data.node.name;
    });
    // if the user has a hoisted tile, we need to add this to the array of sortable items if it doesn't have a pinned index
    if (
      appBarContext.hoistedItem &&
      appBarContext.hoistedItem.data.node.pinnedIndex === undefined
    ) {
      appBarItemIds.push(appBarContext.hoistedItem.data.node.id);
    }
    // return all sortable items for DnD
    return [appBarItemIds, appbarItemsByIdLookUp];
  }, [appBarContext.pinnedItems, appBarContext.hoistedItem]);
  const movePinnedApp = React.useCallback(
    (movingApplicationID: string, movingToApplicationID: string) => {
      if (
        !appBarItemsById.includes(movingApplicationID) ||
        !appBarItemsById.includes(movingToApplicationID)
      ) {
        return;
      }
      if (appBarContext.pinnedItems && appBarContext.pinnedItems.length > 0) {
        // get the index of the application we are moving to
        const movingToIndex = appBarContext.pinnedItems.findIndex((item) => {
          return item.data.node.id === movingToApplicationID;
        });
        if (movingToIndex === -1) {
          return;
        }
        // get the app tile we are moving to
        const movingToApplicationTile =
          appBarContext.pinnedItems[movingToIndex];
        if (!movingToApplicationTile) {
          return;
        }
        // we need the pinned index of the app tile we are moving to
        const movingToPinnedIndex =
          movingToApplicationTile.data.node.pinnedIndex;
        if (movingToPinnedIndex === undefined || movingToPinnedIndex === null) {
          return;
        }
        // update the App Bar Context with the new index of the pinned item
        appBarContext.movePinnedItem(
          movingApplicationID as string,
          movingToIndex
        );
        // update the pinned indexes of the app tiles
        movePinnedM365Application({
          variables: {
            applicationID: movingApplicationID,
            newPinnedIndex: movingToPinnedIndex,
          },
          // We currently won't support a optimistic response here yet, until our Apollo partners
          // are ready to support drag and drop.
        });
      }
    },
    [appBarContext, movePinnedM365Application, appBarItemsById]
  );
  const tileBehavior = controlModelData?.tileBehavior;
  const linkBehavior = controlModelData?.appAcquisitionLinkBehavior;
  const flyoutBehavior = controlModelData?.flyoutBehavior;
  const [flyoutIcon, flyoutIconSelected] = [
    controlModelData?.flyoutIcon ?? undefined,
    controlModelData?.flyoutIconSelected ?? undefined,
  ];
  const enableMicaV2DesktopStyles =
    (tileBehavior?.transparent &&
      controlModelData?.enableMicaV2DesktopStyles) ??
    false;
  const enableMicaV2WebStyles =
    (tileBehavior?.transparent && controlModelData?.enableMicaV2WebStyles) ??
    false;
  const enableMercuryDesignStyles =
    controlModelData?.enableMercuryDesignStyles ?? false;
  const appTilePositionEnabled =
    controlModelData?.appTilePositionEnabled ?? false;
  const isAppbarWide =
    !!tileBehavior?.shouldShowLabel && !enableMercuryDesignStyles;
  const enableAIHub = controlModelData?.enableAIHub ?? false;
  const enableVisualRefreshStyles =
    controlModelData?.enableVisualRefreshStyles ?? false;
  const enableRockSteadyForM365 =
    controlModelData?.enableRockSteadyForM365 ?? false;
  const enableBrandLogo = controlModelData?.enableBrandLogo ?? false;

  const styles = useAppBarStyles();
  const backgroundShadeStyle =
    backgroundShade === "ThreeColumnView"
      ? styles.threeColumnViewBackground
      : styles.twoColumnViewBackground;
  const appBarStyles = mergeClasses(
    styles.appBar,
    backgroundShadeStyle,
    isAppbarWide && styles.wideAppBar,
    enableMercuryDesignStyles && styles.mercuryDesignAppBar
  );
  const dividerStyles = mergeClasses(
    styles.divider,
    backgroundShadeStyle,
    !!tileBehavior?.transparent && styles.transparent
  );
  /**
   * Dynamic media query hook listener using tile heights as thresholds
   * and the divider and children as bottom padding
   */
  const mediaQuery = useDynamicMediaQueryListener(matchMedia);
  /**
   * Effect that passes the render data for variables that won't change
   * often to the dynamic media query hook
   */
  React.useEffect(() => {
    if (mediaQueryReady) {
      return;
    }
    // At least one locked or pinned app DOM element ref needs to be available
    // before we can set the top margin for the media query
    if (
      !appBarContext.lockedItems?.[0]?.elementRef.current &&
      !appBarContext.pinnedItems?.[0]?.elementRef.current
    ) {
      return;
    }
    const tileHeight = calculateTileHeight(
      isAppbarWide,
      enableMercuryDesignStyles,
      enableVisualRefreshStyles
    );
    mediaQuery.setElementHeight(tileHeight);
    mediaQuery.setMinimumVisibleElementCount(MinTiles);
    mediaQuery.setTopMargin(
      appBarContext.lockedItems?.[0]?.elementRef?.current?.getBoundingClientRect()
        .top ??
        appBarContext.pinnedItems?.[0]?.elementRef?.current?.getBoundingClientRect()
          .top ??
        0
    );
    if (enableRockSteadyForM365) {
      setTopMargin(AppBarRockSteadyTopMargin);
    } else if (enableVisualRefreshStyles) {
      setTopMargin(AppBarVisualRefreshTopMargin);
    }
    setMediaQueryReady(true);
  }, [
    appBarContext.lockedItems,
    appBarContext.pinnedItems,
    enableMercuryDesignStyles,
    isAppbarWide,
    mediaQuery,
    mediaQueryReady,
    tileBehavior?.shouldShowLabel,
    enableVisualRefreshStyles,
    enableRockSteadyForM365,
  ]);

  /**
   * Callback to update the top margin value for the media query. Use debounced callback
   * to prevent "ResizeObserver loop completed with undelivered notifications" error
   */
  const appBarResizeCallback = useDebouncedCallback(
    (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        if (mediaQueryReady) {
          mediaQuery.setTopMargin(entry.target.getBoundingClientRect().top);
        }
      }
    },
    10
  );
  /**
   * Callback to sets up the resize observer for the AppBar element
   * so that the top margin can be updated when the AppBar position changes
   */
  const setAppBarRef = React.useCallback(
    (element: HTMLDivElement) => {
      appbarContainerRef.current = element;
      if (!element) {
        return;
      }
      // disconnect any existing resize observer
      if (appBarResizeObserverRef.current) {
        appBarResizeObserverRef.current.disconnect();
      }
      appBarResizeObserverRef.current = new ResizeObserver((entries) => {
        appBarResizeCallback(entries);
      });
      appBarResizeObserverRef.current.observe(element);
    },
    [appBarResizeCallback]
  );
  /**
   * Effect that cleans up any existing resize observer on unmount
   */
  React.useEffect(() => {
    return () => {
      if (appBarResizeObserverRef.current) {
        appBarResizeObserverRef.current.disconnect();
      }
    };
  }, []);

  const { setTopMargin, setBottomPadding } = mediaQuery; // Destructure setTopMargin and setBottomPadding from mediaQuery

  /**
   * Effect that sets bottom padding value for media query hook
   */
  React.useEffect(() => {
    const tileHeight = calculateTileHeight(
      isAppbarWide,
      enableMercuryDesignStyles,
      enableVisualRefreshStyles
    );
    let totalSlidingPanelTileHeight = tileHeight;
    // add the extra tile height if there is a hoisted item
    if (appBarContext.hoistedItem) {
      totalSlidingPanelTileHeight += tileHeight;
    }
    // add tile height for each anchored item
    if (appBarContext.anchoredItems && appBarContext.anchoredItems.length > 0) {
      totalSlidingPanelTileHeight +=
        tileHeight * appBarContext.anchoredItems.length;
    }
    let bottomMargin = AppBarBottomMargin;
    if (enableRockSteadyForM365) {
      bottomMargin = AppBarRockSteadyBottomMargin;
    }
    setBottomPadding(
      AppBarDividerMaxHeightValue + totalSlidingPanelTileHeight + bottomMargin
    );
  }, [
    appBarContext.anchoredItems,
    appBarContext.hoistedItem,
    enableMercuryDesignStyles,
    isAppbarWide,
    tileBehavior?.shouldShowLabel,
    enableVisualRefreshStyles,
    enableRockSteadyForM365,
    setBottomPadding,
  ]);

  /**
   * Sets the number of possible elements that can be visible to the media query hook
   */
  React.useEffect(() => {
    const lockedItemCount = appBarContext.lockedItems?.length ?? 0;
    const pinnedItemCount = appBarContext.pinnedItems?.length ?? 0;
    mediaQuery.setElementCount(lockedItemCount + pinnedItemCount);
  }, [appBarContext.lockedItems, appBarContext.pinnedItems, mediaQuery]);
  /**
   * Updates selection bar position and color
   */
  React.useEffect(() => {
    if (!selectionBar.current) {
      return;
    }
    const tileRef =
      appBarContext.hoistedItem?.elementRef ??
      appBarContext.selectedItem?.elementRef;
    let visibility = true;
    if (tileRef?.current) {
      selectionBar.current.setPosition(tileRef.current.offsetTop);
    } else if (
      !!flyoutBehavior?.selectFlyoutButtonWhenNoAppsSelected &&
      flyoutButtonRef.current
    ) {
      selectionBar.current.setPosition(flyoutButtonRef.current.offsetTop);
    } else {
      visibility = false;
    }
    selectionBar.current.setVisible(visibility);
  }, [
    selectionBar,
    flyoutButtonRef,
    appBarContext.selectedItem?.elementRef,
    appBarContext.hoistedItem?.elementRef,
    appBarContext.hoistedItem,
    flyoutBehavior?.selectFlyoutButtonWhenNoAppsSelected,
  ]);
  /**
   * Callback to show the tile context menu
   *
   * @param target Target element to use as the context menu anchor
   * @param pinnedIndex Pinned index value of element or undefined if not pinned
   * @param commands Array of commands supported by the tile
   * @param targetAppTileId The ID of the tile that the context menu is being shown for
   */
  const showContextMenu = React.useCallback(
    (
      target: HTMLElement,
      pinnedIndex: number | undefined,
      commands: TileContextMenuCallbacks,
      targetAppTileId: string
    ) => {
      if (contextMenuRef?.current) {
        contextMenuRef.current.open(
          target,
          pinnedIndex,
          commands,
          targetAppTileId
        );
      }
    },
    []
  );
  /**
   * Callback to manage the pin state changes of the launch tiles the AppBar hosts.
   * @param id The ID of the application that changed pin state.
   * @param pinStatus The new pin status of the application.
   */
  const pinStateChangedCallback = React.useCallback(
    (id: string, pinStatus: boolean) => {
      if (pinStatus) {
        pinApp(id);
        // After an app has been pinned focus it for keyboard users and ensure it is announced by screen readers
        appbarContainerRef.current &&
          waitForElement(
            id,
            "button",
            { "data-pinned": "true" },
            appbarContainerRef.current
          ).then((element) => element?.focus());
      } else {
        unpinApp(id);
      }
    },
    [pinApp, unpinApp]
  );
  /**
   * Sets the overflowIndex in context based on the visibility
   * vector from the media query hook
   */
  React.useLayoutEffect(() => {
    let hiddenStart = mediaQuery.visibilityVector.indexOf(false);
    // if all values are true, overflow starts after the last tile
    if (hiddenStart === -1) {
      hiddenStart = mediaQuery.visibilityVector.length;
    }
    // don't set overflowIndex if we don't have a full visibility vector yet
    if (hiddenStart === 0) {
      return;
    }
    appBarContext.setOverflowIndex(hiddenStart);
  }, [appBarContext, mediaQuery.visibilityVector]);
  const novaEventing = useNovaEventing();
  /**
   * Builds a launch tile for display on the AppBar or Flyout
   *
   * @param m365AppEdge Tile data used to render tile
   * @param index Index of tile related to other tiles
   * @param mountTo Portal element to render to (eg. Flyout)
   */
  const renderLaunchTile = useCallback(
    (appBarItem: AppBarItem, index: number) => {
      if (!appBarItem.data.node) {
        return null;
      }
      const launchTilePinStateChanged = (pinStatus: boolean) => {
        pinStateChangedCallback(appBarItem.data.node.id, pinStatus);
      };
      const m365App = appBarItem.data.node;
      const launchTile = tileBehavior && (
        <LaunchTile
          index={index}
          viewMode={isAppbarWide ? ViewMode.Wide : ViewMode.Default}
          key={m365App.id}
          m365App={m365App}
          launchTileBehavior={tileBehavior}
          assignShortcutHandler={assignShortcutHandler}
          unassignShortcutHandler={unassignShortcutHandler}
          ref={appBarItem.itemRef}
          onContextMenu={showContextMenu}
          onPinStateChange={launchTilePinStateChanged}
          enableMicaV2DesktopStyles={enableMicaV2DesktopStyles}
          enableMicaV2WebStyles={enableMicaV2WebStyles}
          enableMercuryDesignStyles={enableMercuryDesignStyles}
          dragDropEnabled={!!tileBehavior.dragDropEnabled}
          backgroundShade={backgroundShade}
          enableVisualRefreshStyles={enableVisualRefreshStyles}
        />
      );
      const onMouseEnter = () => {
        if (m365App.id === appBarContext.selectedItem?.data.node.id) {
          // Fire the Nova event, then call the prop callback
          novaEventing.generateEvent({
            event: AppBarEvents.appTileHover(() => {
              const isHoisted = appBarContext.overflowIndex === index;
              const selectedAppItem = isHoisted
                ? appBarContext.hoistedItem
                : appBarContext.selectedItem;
              return {
                id: m365App.id,
                isHovered: true,
                tilePosition: index,
                tilePositionVertical: appTilePositionEnabled
                  ? selectedAppItem?.itemRef.current?.getAppIconPositionRect()
                      ?.top
                  : undefined,
              };
            }),
          });
          if (onAppTileHoverEnter) {
            onAppTileHoverEnter();
          }
        }
      };
      const onMouseLeave = () => {
        if (m365App.id === appBarContext.selectedItem?.data.node.id) {
          // Fire the Nova event, then call the prop callback
          novaEventing.generateEvent({
            event: AppBarEvents.appTileHover(() => {
              const isHoisted = appBarContext.overflowIndex === index;
              const selectedAppItem = isHoisted
                ? appBarContext.hoistedItem
                : appBarContext.selectedItem;
              return {
                id: m365App.id,
                isHovered: false,
                tilePosition: index,
                tilePositionVertical: appTilePositionEnabled
                  ? selectedAppItem?.itemRef.current?.getAppIconPositionRect()
                      ?.top
                  : undefined,
              };
            }),
          });
          if (onAppTileHoverLeave) {
            onAppTileHoverLeave();
          }
        }
      };
      return (
        <div
          key={m365App.id}
          ref={appBarItem.elementRef}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {launchTile}
        </div>
      );
    },
    [
      tileBehavior,
      isAppbarWide,
      assignShortcutHandler,
      unassignShortcutHandler,
      showContextMenu,
      enableMicaV2DesktopStyles,
      enableMicaV2WebStyles,
      enableMercuryDesignStyles,
      backgroundShade,
      pinStateChangedCallback,
      appBarContext.selectedItem,
      appBarContext.hoistedItem,
      appBarContext.overflowIndex,
      novaEventing,
      onAppTileHoverEnter,
      appTilePositionEnabled,
      onAppTileHoverLeave,
      enableVisualRefreshStyles,
    ]
  );
  const handleDragEnd = React.useCallback(
    (movingApplicationID: string, movingToApplicationID: string) => {
      // if the moving id and moving to id are the same then we don't need to run the pin logic
      // just set dndInUse state to false after the move has ended
      if (movingApplicationID !== movingToApplicationID) {
        movePinnedApp(movingApplicationID, movingToApplicationID);
      }
      // set dndInUse state to false after the move has ended
      setDnDInUse(false);
      // reset the divider visible state to true after move has ended
      setDividerVisibleState(true);
    },
    [movePinnedApp]
  );
  const handleDragStart = React.useCallback(() => {
    // apply effects for when dnd has been started activated by the user
    // set dnd in use to true on move
    setDnDInUse(true);
    setDividerVisibleState(false);
  }, []);
  const handleDragCancel = React.useCallback(() => {
    // apply effects for when dnd has been cancelled by the user
    // set dnd in use to false on cancel
    setDnDInUse(false);
    setDividerVisibleState(true);
  }, []);
  /**
   * Callback to render the locked launch tiles when launch tile data is updated
   */
  const renderLockedItems = useCallback(() => {
    const visibleTiles = appBarContext.lockedItems?.slice(
      0,
      appBarContext.overflowIndex
    );
    return visibleTiles?.map((tile: AppBarItem, index: number) => {
      return renderLaunchTile(tile, index);
    });
  }, [
    appBarContext.lockedItems,
    appBarContext.overflowIndex,
    renderLaunchTile,
  ]);
  /**
   * Callback to render the anchored launch tiles when launch tile data is updated
   */
  const renderAnchoredItems = useCallback(() => {
    return appBarContext.anchoredItems?.map(
      (tile: AppBarItem, index: number) => {
        return renderLaunchTile(
          tile,
          index + (appBarContext.overflowIndex ?? 0)
        );
      }
    );
  }, [
    appBarContext.anchoredItems,
    appBarContext.overflowIndex,
    renderLaunchTile,
  ]);
  const renderPinnedItems = useCallback(() => {
    if (!appBarContext?.lockedItems) {
      return;
    }
    const startIndex = appBarContext?.lockedItems.length;
    if (
      appBarContext.overflowIndex !== undefined &&
      appBarContext.overflowIndex < startIndex
    ) {
      return;
    }
    const offsetOverflow =
      appBarContext.overflowIndex !== undefined
        ? appBarContext.overflowIndex - appBarContext.lockedItems.length
        : undefined;
    const visibleTiles = appBarContext.pinnedItems?.slice(0, offsetOverflow);
    return visibleTiles?.map((tile: AppBarItem, index: number) => {
      return renderLaunchTile(tile, index + startIndex);
    });
  }, [
    appBarContext.lockedItems,
    appBarContext.overflowIndex,
    appBarContext.pinnedItems,
    renderLaunchTile,
  ]);
  /**
   * Callback to render the hoisted launchtile if present
   */
  const renderHoistedItem = React.useCallback(() => {
    // get the correct index for the hoisted item
    const hoistedItemIndex = appBarContext.overflowIndex ?? 0;
    if (appBarContext.hoistedItem) {
      return renderLaunchTile(appBarContext.hoistedItem, hoistedItemIndex);
    }
    return undefined;
  }, [
    appBarContext.hoistedItem,
    appBarContext.overflowIndex,
    renderLaunchTile,
  ]);
  /**
   * Callback used by AppBar consumers to dispatch actions after render
   */
  React.useImperativeHandle(
    forwardRef,
    () => ({
      focus: () => {
        appBarContext.selectedItem?.itemRef.current?.focus();
      },
      getAppIconPositionRect: (id?: string) => {
        if (!id || id === appBarContext.selectedItem?.data.node.id) {
          return appBarContext.selectedItem?.itemRef.current?.getAppIconPositionRect();
        }
        return appBarContext.lockedItems
          ?.concat(appBarContext.pinnedItems ?? [])
          ?.find((item) => {
            return item.data.node.id === id;
          })
          ?.itemRef.current?.getAppIconPositionRect();
      },
      getAppIconElementRef: (id?: string) => {
        if (!id || id === appBarContext.selectedItem?.data.node.id) {
          return appBarContext.selectedItem?.itemRef?.current?.getAppIconElementRef();
        }
        return id === FlyoutTileID
          ? flyoutButtonRef
          : appBarContext.lockedItems
              ?.concat(appBarContext.pinnedItems ?? [])
              ?.find((item) => {
                return item.data.node.id === id;
              })
              ?.itemRef?.current?.getAppIconElementRef();
      },
    }),
    [
      flyoutButtonRef,
      appBarContext.lockedItems,
      appBarContext.pinnedItems,
      appBarContext.selectedItem?.data.node.id,
      appBarContext.selectedItem?.itemRef,
    ]
  );
  const appBarSortableContent = React.useMemo(() => {
    return (
      <>
        {renderPinnedItems()}
        {appBarContext.hoistedItem && !enableAIHub && (
          <>
            {dividerVisibleState && <Divider inset className={dividerStyles} />}
            <div>{renderHoistedItem()}</div>
          </>
        )}
      </>
    );
  }, [
    appBarContext.hoistedItem,
    dividerStyles,
    dividerVisibleState,
    enableAIHub,
    renderHoistedItem,
    renderPinnedItems,
  ]);
  // Lazy renderer for the DnDContext
  const LazyDnDContextRenderer = React.useRef(
    React.lazy(() =>
      customImportHandler(
        import(
          /* webpackChunkName: "appbar-control-lazydndcontext" */ "./LazyDnDContext"
        )
      )
    )
  );
  const sortableItems = React.useMemo(() => {
    const LazyDnDContext = LazyDnDContextRenderer.current;
    const a11yStrings = {
      onDragStartText: controlModelData?.localizedStrings.onDragStartText || "",
      onDragEndText: controlModelData?.localizedStrings.onDragEndText || "",
      onDragOverText: controlModelData?.localizedStrings.onDragOverText || "",
      onDragCancelText:
        controlModelData?.localizedStrings.onDragCancelText || "",
      onDragAtTopText: controlModelData?.localizedStrings.onDragAtTopText || "",
      onDragAtBottomText:
        controlModelData?.localizedStrings.onDragAtBottomText || "",
    };
    return appBarContext.pinnedItems &&
      appBarContext.pinnedItems.length > 0 &&
      tileBehavior?.dragDropEnabled ? (
      <AppBarErrorBoundary boundarySource="AppbarDnDContext">
        <React.Suspense fallback="">
          <LazyDnDContext
            sortableItemsById={appBarItemsById}
            appbarItemsByIdLookUp={appbarItemsByIdLookUp}
            handleDragEndCallback={handleDragEnd}
            handleDragStartCallback={handleDragStart}
            handleDragCancelCallback={handleDragCancel}
            a11yStrings={a11yStrings}
          >
            {appBarSortableContent}
          </LazyDnDContext>
        </React.Suspense>
      </AppBarErrorBoundary>
    ) : (
      appBarSortableContent
    );
  }, [
    appBarContext.pinnedItems,
    appBarItemsById,
    appbarItemsByIdLookUp,
    appBarSortableContent,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    tileBehavior?.dragDropEnabled,
    controlModelData?.localizedStrings,
  ]);
  const showFlyoutButton =
    (appBarContext.overflowItems && appBarContext.overflowItems.length > 0) ||
    (appBarContext.appBarData?.m365Apps?.appAcquisitionLinks &&
      appBarContext.appBarData?.m365Apps?.appAcquisitionLinks?.length > 0);
  // Enable the tile selection bar
  const enableTileSelectionBar =
    !dndInUse &&
    appBarContext.selectedItem &&
    !enableMercuryDesignStyles &&
    !enableVisualRefreshStyles;

  const shouldCenterAlign =
    enableAIHub || (enableVisualRefreshStyles && !enableRockSteadyForM365);

  return React.useMemo(
    () => (
      <>
        {controlModelData && tileBehavior && (
          <FlexPanel
            ref={setAppBarRef}
            transparent={!!tileBehavior.transparent}
            className={appBarStyles}
            backgroundShadeClass={backgroundShadeStyle}
            keyNavigationDirection={dndInUse ? undefined : KeyNavDir.vertical}
            memorizeCurrent={true}
          >
            {enableBrandLogo && (
              <div
                className={mergeClasses(
                  styles.tileContainer,
                  isAppbarWide && styles.tileContainerWide
                )}
              >
                {brandLogo}
              </div>
            )}
            <FlexPanel
              className={mergeClasses(
                getAppBarClassname(isAppbarWide, shouldCenterAlign, styles),
                enableVisualRefreshStyles && styles.verticalGap
              )}
              role="navigation"
              transparent={!!tileBehavior.transparent}
              aria-label={controlModelData.rootNavigationAriaLabel || undefined}
              mercuryDesignEnabled={enableMercuryDesignStyles}
            >
              <LazyTileContextMenu
                ref={contextMenuRef}
                tileContextMenu={tileBehavior.tileContextMenu}
                customImportHandler={customImportHandler}
              >
                <>
                  {renderLockedItems()}
                  {sortableItems}
                </>
              </LazyTileContextMenu>
            </FlexPanel>
            <FlexPanel fill transparent={!!tileBehavior.transparent}>
              {showFlyoutButton && (
                <FlyoutButton
                  customImportHandler={customImportHandler}
                  selected={
                    !appBarContext.selectedItem &&
                    !!flyoutBehavior?.selectFlyoutButtonWhenNoAppsSelected
                  }
                  ref={flyoutButtonRef}
                  icon={flyoutIcon}
                  selectedIcon={flyoutIconSelected}
                  text={
                    controlModelData?.localizedStrings.flyoutButtonText ?? ""
                  }
                  title={
                    controlModelData?.localizedStrings.flyoutButtonTitle ?? ""
                  }
                  emptyStateMessage={
                    controlModelData?.localizedStrings
                      .flyoutEmptyStateMessage ?? ""
                  }
                  searchBoxPlaceholderText={
                    controlModelData?.flyoutBehavior?.searchBehavior
                      ?.placeholderText ?? ""
                  }
                  suggestedAppsPrivacyLinkText={
                    controlModelData?.localizedStrings
                      .flyoutSuggestedAppsPrivacyLinkText ?? ""
                  }
                  suggestedAppsPermissionsLinkText={
                    controlModelData?.localizedStrings
                      .flyoutSuggestedAppsPermissionsLinkText ?? ""
                  }
                  suggestedAppsTermsOfUseLinkText={
                    controlModelData?.localizedStrings
                      .flyoutSuggestedAppsTermsOfUseLinkText ?? ""
                  }
                  suggestedAppsAddButtonText={
                    controlModelData?.localizedStrings
                      .flyoutSuggestedAppsAddButtonText ?? ""
                  }
                  suggestedAppsSectionTitle={
                    controlModelData?.localizedStrings
                      .flyoutSuggestedAppsSectionTitle ?? undefined
                  }
                  quickAccessSectionTitle={
                    controlModelData?.localizedStrings
                      .flyoutQuickAccessSectionTitle ?? undefined
                  }
                  seeAllButtonLabel={
                    controlModelData?.localizedStrings
                      .flyoutSeeAllButtonLabel ?? ""
                  }
                  installedAppsAnnouncementMessage={
                    controlModelData?.localizedStrings
                      .flyoutInstalledAppsSearchResultsAnnouncement ?? undefined
                  }
                  suggestedAppsAnnouncementMessage={
                    controlModelData?.localizedStrings
                      .flyoutSuggestedAppsSearchResultsAnnouncement ?? undefined
                  }
                  installedAndSuggestedAppsAnnouncementMessage={
                    controlModelData?.localizedStrings
                      .flyoutInstalledAndSuggestedAppsSearchResultsAnnouncement ??
                    undefined
                  }
                  wide={isAppbarWide}
                  footerLinks={
                    appBarContext.appBarData?.m365Apps?.appAcquisitionLinks
                  }
                  enableVisualRefreshStyles={enableVisualRefreshStyles}
                  launchTileBehavior={tileBehavior}
                  flyoutSize={flyoutBehavior?.flyoutSize || "Large"}
                  enabledFlyoutButtonBadgeType={
                    controlModelData?.flyoutBehavior
                      ?.enabledFlyoutButtonBadgeType || "None"
                  }
                  flyoutButtonBadgeDescription={
                    controlModelData?.localizedStrings
                      ?.flyoutButtonBadgeDescription ?? undefined
                  }
                  footerLinkFormat={linkBehavior?.linkFormat ?? undefined}
                  searchEnabled={
                    !!controlModelData?.flyoutBehavior?.searchBehavior
                      ?.isEnabled
                  }
                  suggestionsEnabled={
                    !!controlModelData?.flyoutBehavior
                      ?.isSuggestionsSectionEnabled
                  }
                  quickAccessEnabled={
                    !!controlModelData?.flyoutBehavior
                      ?.isQuickAccessSectionEnabled
                  }
                  seeAllButtonEnabled={
                    !!controlModelData?.flyoutBehavior?.isSeeAllButtonEnabled
                  }
                  seeAllButtonIndex={
                    controlModelData?.flyoutBehavior?.seeAllButtonIndex
                  }
                  searchDebounceRate={
                    controlModelData?.flyoutBehavior?.searchBehavior
                      ?.debounceRate
                  }
                  searchNoResultsTitle={
                    controlModelData?.flyoutBehavior?.searchBehavior
                      ?.noResultsTitle
                  }
                  searchNoResultsMessage={
                    controlModelData?.flyoutBehavior?.searchBehavior
                      ?.noResultsMessage
                  }
                  searchResultsSectionTitle={
                    controlModelData?.flyoutBehavior?.searchBehavior
                      ?.resultsSectionTitle ?? undefined
                  }
                  quickAccessSearchResultsSectionTitle={
                    controlModelData?.flyoutBehavior?.searchBehavior
                      ?.quickAccessResultsSectionTitle ?? undefined
                  }
                  installedAppsSectionAriaLabel={
                    controlModelData?.localizedStrings
                      ?.flyoutInstalledAppsSectionAriaLabel ?? undefined
                  }
                  searchResultsAriaLiveAnnouncement={
                    controlModelData?.localizedStrings
                      ?.flyoutSearchResultsAriaLiveAnnouncement ?? undefined
                  }
                  suggestedAppsSectionAriaLabel={
                    controlModelData?.localizedStrings
                      ?.flyoutSuggestedAppsSectionAriaLabel ?? undefined
                  }
                  quickAccessSectionAriaLabel={
                    controlModelData?.localizedStrings
                      ?.flyoutQuickAccessSectionAriaLabel ?? undefined
                  }
                  onPinStateChange={pinStateChangedCallback}
                />
              )}
              {renderAnchoredItems()}
            </FlexPanel>
            {enableTileSelectionBar && (
              <TileSelectionBar
                wide={isAppbarWide}
                animate={!!controlModelData.animateSelection}
                initialVisibility={false}
                ref={selectionBar}
              />
            )}

            <AppBarImpression
              impressionStabilizeTimeout={
                controlModelData.impressionStabilizeTimeout || undefined
              }
            />
          </FlexPanel>
        )}
      </>
    ),
    [
      brandLogo,
      controlModelData,
      tileBehavior,
      setAppBarRef,
      appBarStyles,
      backgroundShadeStyle,
      dndInUse,
      isAppbarWide,
      enableBrandLogo,
      shouldCenterAlign,
      enableTileSelectionBar,
      styles,
      enableMercuryDesignStyles,
      enableVisualRefreshStyles,
      customImportHandler,
      renderLockedItems,
      sortableItems,
      showFlyoutButton,
      appBarContext.selectedItem,
      appBarContext.appBarData?.m365Apps?.appAcquisitionLinks,
      flyoutBehavior?.selectFlyoutButtonWhenNoAppsSelected,
      flyoutBehavior?.flyoutSize,
      flyoutIcon,
      flyoutIconSelected,
      linkBehavior?.linkFormat,
      pinStateChangedCallback,
      renderAnchoredItems,
      selectionBar,
    ]
  );
};
/**
 * Encapsulates and provides context to the main AppBar render object
 * @param props Props for the AppBar control
 * @param ref Forward ref to provide additional functionality for hosting apps
 * @returns Forward ref render function for AppBar with context
 */
export const AppBarWithContext: React.ForwardRefRenderFunction<
  AppBarInteractivity,
  AppBarProps
> = (props, ref) => {
  return (
    <AppBarErrorBoundary boundarySource="Appbar">
      <AppBarContextProvider>
        <AppBarRender {...props} forwardRef={ref} />
      </AppBarContextProvider>
    </AppBarErrorBoundary>
  );
};
export const AppBar = React.forwardRef(AppBarWithContext);
const calculateTileHeight = (
  isAppbarWide: boolean,
  enableMercuryDesignStyles: boolean,
  enableVisualRefreshStyles: boolean
): number => {
  if (enableVisualRefreshStyles) {
    return TileHeightVisualRefreshValue;
  }
  return isAppbarWide
    ? TileHeightWideValue
    : enableMercuryDesignStyles
      ? TileHeightMercuryValue
      : TileDimensionDefaultValue + 2 * TileMarginDefaultValue;
};
function getAppBarClassname(
  isAppbarWide: boolean,
  shouldCenterAlign: boolean,
  styles: Record<
    | "tileContainer"
    | "tileContainerCenterAligned"
    | "tileContainerWide"
    | "tileContainerWideCenterAligned",
    string
  >
): string {
  if (shouldCenterAlign)
    return isAppbarWide
      ? styles.tileContainerWideCenterAligned
      : styles.tileContainerCenterAligned;
  if (isAppbarWide) return styles.tileContainerWide;
  else return styles.tileContainer;
}
// Uses MutationObserver to wait for an element to be added to the DOM
async function waitForElement(
  id: string,
  role: string,
  additionalAttributes: {
    [key: string]: string;
  },
  target: Node
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const element = document.getElementById(id);
      if (
        element &&
        element.getAttribute("role") === role &&
        Object.keys(additionalAttributes).every(
          (key) => element.getAttribute(key) === additionalAttributes[key]
        )
      ) {
        observer.disconnect();
        resolve(element);
      }
    });
    observer.observe(target, {
      childList: true,
      subtree: true,
    });
    // Back up to ensure the observer is disconnected
    const maxTimeout = 3000;
    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, maxTimeout);
  });
}
